import React from 'react';
import { rem } from 'polished';
import {
  LandingSkel,
  HeaderSectionSkeletonRoot,
  HeaderSectionSkeletonMain,
  HeaderSectionSkeletonActions,
  SearchSkel,
  ActionBtnSkel,
  CatalogSkeletonShell,
  CatalogSkeletonHeadRow,
  CatalogSkeletonBody,
  CatalogSkeletonRow,
  CatalogSkeletonCell,
  CatalogSkeletonFooter,
  BottomActionsSkeleton,
  ProductsPageSkeletonShell,
  ProductsCrumbSkeletonStrip,
  ProductsPageSkeletonBody,
  ProductsPageSkeletonIntro,
  ProductsPageSkeletonMain
} from './ProductsLandingSkeleton.styles';

const CATALOG_SKEL_ROW_COUNT = 8;

/** Título, última actualización y toolbar (search + botones). */
export function HeaderSectionSkeleton() {
  return (
    <HeaderSectionSkeletonRoot role="status" aria-busy="true" aria-label="Loading">
      <HeaderSectionSkeletonMain>
        <LandingSkel $height={rem('16px')} $width="240px" />
        <LandingSkel $height={rem('12px')} $width="320px" $maxWidth="100%" />
      </HeaderSectionSkeletonMain>
      <HeaderSectionSkeletonActions>
        <SearchSkel />
        <ActionBtnSkel />
        <ActionBtnSkel />
      </HeaderSectionSkeletonActions>
    </HeaderSectionSkeletonRoot>
  );
}

/** Skeleton de tabla de catálogo con columnas alineadas al listado real. */
export function CatalogTableSkeleton({
  ariaLabel = 'Loading',
  colWidths,
  embedded = false
}) {
  const widths = colWidths || ['4%', '6%', '9%', '36%', '21%', '10%', '14%'];
  const gridColumns = widths.map(w => `minmax(0, ${w})`).join(' ');

  const content = (
    <>
      <CatalogSkeletonHeadRow $columns={gridColumns}>
        {widths.map((_, i) => (
          <CatalogSkeletonCell key={i} $align={i === 0 ? 'center' : 'flex-start'}>
            {i === 0 ? (
              <LandingSkel $height={rem('14px')} $width={rem('14px')} />
            ) : (
              <LandingSkel
               
                $height={rem('10px')}
                $width={i === widths.length - 1 ? '60%' : '72%'}
              />
            )}
          </CatalogSkeletonCell>
        ))}
      </CatalogSkeletonHeadRow>
      <CatalogSkeletonBody>
        {Array.from({ length: CATALOG_SKEL_ROW_COUNT }).map((_, ri) => (
          <CatalogSkeletonRow key={ri} $columns={gridColumns}>
            <CatalogSkeletonCell $align="center">
              <LandingSkel $height={rem('18px')} $width={rem('18px')} $delay={`${ri * 0.06}s`} />
            </CatalogSkeletonCell>
            <CatalogSkeletonCell $align="center">
              <LandingSkel $height={rem('44px')} $width={rem('44px')} $radius={rem('6px')} />
            </CatalogSkeletonCell>
            <CatalogSkeletonCell>
              <LandingSkel $height={rem('12px')} $width="80%" />
            </CatalogSkeletonCell>
            <CatalogSkeletonCell>
              <div style={{ width: '100%', minWidth: 0 }}>
                <LandingSkel $height={rem('12px')} $width="88%" $delay={`${ri * 0.04}s`} />
                <LandingSkel
                 
                  $height={rem('10px')}
                  $width="55%"
                  $maxWidth={rem('220px')}
                  style={{ marginTop: rem('6px') }}
                />
              </div>
            </CatalogSkeletonCell>
            <CatalogSkeletonCell>
              <LandingSkel $height={rem('24px')} $width="78%" $radius={rem('6px')} />
            </CatalogSkeletonCell>
            <CatalogSkeletonCell>
              <LandingSkel $height={rem('28px')} $width={rem('44px')} $radius={rem('14px')} />
            </CatalogSkeletonCell>
            <CatalogSkeletonCell $align="flex-end">
              <LandingSkel $height={rem('28px')} $width={rem('28px')} $radius="50%" />
              <LandingSkel
               
                $height={rem('28px')}
                $width={rem('28px')}
                $radius="50%"
                style={{ marginLeft: rem('8px') }}
              />
            </CatalogSkeletonCell>
          </CatalogSkeletonRow>
        ))}
      </CatalogSkeletonBody>
      {!embedded && (
        <CatalogSkeletonFooter>
          <LandingSkel $height={rem('12px')} $width="120px" />
          <LandingSkel $height={rem('12px')} $width="160px" />
          <LandingSkel $height={rem('12px')} $width="180px" style={{ justifySelf: 'end' }} />
        </CatalogSkeletonFooter>
      )}
    </>
  );

  if (embedded) {
    return (
      <div role="status" aria-busy="true" aria-label={ariaLabel} style={{ width: '100%' }}>
        {content}
      </div>
    );
  }

  return (
    <CatalogSkeletonShell role="status" aria-busy="true" aria-label={ariaLabel}>
      {content}
    </CatalogSkeletonShell>
  );
}

/** Botones inferiores (Agregar / Restaurar / Aceptar). */
export function BottomActionsSectionSkeleton() {
  return (
    <>
      <LandingSkel $height={rem('36px')} $width={rem('140px')} $radius={rem('6px')} />
      <LandingSkel $height={rem('36px')} $width={rem('110px')} $radius={rem('6px')} />
      <LandingSkel $height={rem('36px')} $width={rem('100px')} $radius={rem('6px')} />
    </>
  );
}

/**
 * Skeleton completo de /products para Suspense (dark + light).
 * Incluye breadcrumb + header + tabla + acciones.
 */
export function ProductsLandingPageSkeleton({ colWidths, ariaLabel = 'Loading' }) {
  return (
    <ProductsPageSkeletonShell role="status" aria-busy="true" aria-label={ariaLabel}>
      <ProductsCrumbSkeletonStrip>
        <LandingSkel $height="12px" $width="90px" />
        <LandingSkel $height="12px" $width="140px" />
      </ProductsCrumbSkeletonStrip>
      <ProductsPageSkeletonBody>
        <ProductsPageSkeletonIntro>
          <HeaderSectionSkeleton />
        </ProductsPageSkeletonIntro>
        <ProductsPageSkeletonMain>
          <CatalogTableSkeleton colWidths={colWidths} ariaLabel={ariaLabel} />
          <BottomActionsSkeleton>
            <BottomActionsSectionSkeleton />
          </BottomActionsSkeleton>
        </ProductsPageSkeletonMain>
      </ProductsPageSkeletonBody>
    </ProductsPageSkeletonShell>
  );
}
